import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { PermissionKey } from '@/types/roles';

interface UserPermissionsResult {
  permissions: PermissionKey[];
  isLoading: boolean;
  error: Error | null;
}

/**
 * Fetches all enabled permissions for the current user.
 * Returns empty array if user has no role or permissions.
 * Fail-closed: returns empty array on error or loading.
 *
 * The legacy `role = 'ADMIN'` text bypass has been retired on both the client
 * and in the database (`has_any_permission`). Authorization is now derived
 * exclusively from the assigned role's `role_permissions` rows.
 * 
 * Query strategy: 
 * 1. Get user's profile to find their role_id
 * 2. Get all enabled permissions for that role
 */
export function useUserPermissions(): UserPermissionsResult {
  const { user, profile } = useAuth();
  const roleId = profile?.role_id;

  if (user?.id && profile && !roleId) {
    console.warn('[useUserPermissions] User profile has no role_id assigned. Permissions will be empty.', {
      userId: user.id,
      profileId: profile.id,
      legacyRole: profile.role
    });
  }
  
  const { data, isLoading, error } = useQuery({
    queryKey: ['user-permissions', user?.id, roleId],
    queryFn: async (): Promise<PermissionKey[]> => {
      if (!user?.id || !roleId) return [];
      
      // Fetch all enabled permissions for the user's role
      const { data: permissions, error: fetchError } = await supabase
        .from('role_permissions')
        .select('permission_key')
        .eq('role_id', roleId)
        .eq('enabled', true);
      
      if (fetchError) {
        console.error('Error fetching user permissions:', fetchError);
        throw fetchError;
      }
      
      // Extract permission keys
      return (permissions || []).map(p => p.permission_key as PermissionKey);
    },
    enabled: !!user?.id && !!roleId,
    staleTime: 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    permissions: data || [],
    isLoading,
    error: error as Error | null,
  };
}
